/**
 * Error Toast Component for AudioRecorder
 * 
 * Displays user-friendly error messages with recovery suggestions
 * 
 * Part of Native Voice Capture feature (Epic 13)
 * Task 2.8: Web Error Handling
 */

import React, { useEffect, useState } from 'react';
import {
  AudioRecorderErrorDetails,
  getErrorIcon,
  formatErrorMessage,
  formatErrorSuggestions,
} from './AudioRecorderErrors';
import styles from './ErrorToast.module.css';

export interface ErrorToastProps {
  /** Error details to display */
  error: AudioRecorderErrorDetails | null;
  /** Callback when toast is dismissed */
  onDismiss?: () => void;
  /** Auto-dismiss after ms (0 = no auto-dismiss) */
  autoDismiss?: number;
  /** Show detailed suggestions */
  showSuggestions?: boolean;
  /** Retry callback (if error is recoverable) */
  onRetry?: () => void;
}

/**
 * ErrorToast Component
 */
export const ErrorToast: React.FC<ErrorToastProps> = ({
  error,
  onDismiss,
  autoDismiss = 0,
  showSuggestions = true,
  onRetry,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Show/hide based on error
  useEffect(() => {
    if (error) {
      setIsVisible(true);
      setIsExpanded(false);

      // Auto-dismiss for warnings
      if (autoDismiss > 0 && error.severity === 'warning') {
        const timer = setTimeout(() => {
          handleDismiss();
        }, autoDismiss);
        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }
  }, [error, autoDismiss]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onDismiss) {
        onDismiss();
      }
    }, 300); // Wait for exit animation
  };

  const handleRetry = () => {
    if (onRetry) {
      handleDismiss();
      onRetry();
    }
  };

  if (!error || !isVisible) {
    return null;
  }

  const icon = getErrorIcon(error);
  const message = formatErrorMessage(error);
  const suggestions = formatErrorSuggestions(error);

  return (
    <div
      className={`${styles.toast} ${styles[error.severity]} ${isVisible ? styles.visible : ''}`}
      role="alert"
      aria-live="assertive"
    >
      <div className={styles.header}>
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
        <span className={styles.message}>{message}</span>
        <button
          className={styles.dismissButton}
          onClick={handleDismiss}
          aria-label="Dismiss error"
        >
          ✕
        </button>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className={styles.body}>
          <button
            className={styles.expandButton}
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
          >
            {isExpanded ? '▼' : '▶'} What can I do?
          </button>

          {isExpanded && (
            <ul className={styles.suggestions}>
              {suggestions.map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error.recoverable && onRetry && (
        <div className={styles.actions}>
          <button className={styles.retryButton} onClick={handleRetry}>
            Try Again
          </button>
        </div>
      )}

      {process.env.NODE_ENV === 'development' && error.technicalDetails && (
        <details className={styles.technical}>
          <summary>Technical Details</summary>
          <pre>{error.technicalDetails}</pre>
        </details>
      )}
    </div>
  );
};

/**
 * Hook for managing error toast
 */
export function useErrorToast() {
  const [error, setError] = useState<AudioRecorderErrorDetails | null>(null);

  const showError = (errorDetails: AudioRecorderErrorDetails) => {
    setError(errorDetails);
  };

  const hideError = () => {
    setError(null);
  };

  return {
    error,
    showError,
    hideError,
  };
}

export default ErrorToast;

